import React, { useState, useEffect } from 'react';
import { Dimmer, Loader } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import TurnstileWidget from '../common/TurnstileWidget';
import { useDispatch } from 'react-redux';
import { useLoginMutation, useRegisterMutation, useGetGoogleAuthUrlQuery, useGetAppleAuthUrlQuery, useNativeLoginMutation } from '../../redux/apis/endpoints/authApi';
import { setCredentials as setAuthCredentials } from '../../redux/slices/authSlice';
import './VTOLoginConfirmationPortal.css';

const VTOLoginConfirmationPortal = ({
  isOpen = false,
  onLogin,
  onCancel,
  onClose
}) => {
  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'
  const [credentials, setCredentials] = useState({ identifier: '', password: '' });
  const [signupData, setSignupData] = useState({ username: '', email: '', password: '', passwordConfirm: '' });
  // Turnstile token for email/password signup. Single-use, ~5min lifetime.
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaReset, setCaptchaReset] = useState(0);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [shouldFetchGoogleUrl, setShouldFetchGoogleUrl] = useState(false);
  const [shouldFetchAppleUrl, setShouldFetchAppleUrl] = useState(false);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isConnectingApple, setIsConnectingApple] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [register, { isLoading: isRegistering }] = useRegisterMutation();
  const [nativeLogin] = useNativeLoginMutation();
  const dispatch = useDispatch();

  // Google Auth
  const { data: googleAuthData, isError: isGoogleError, error: googleError } = useGetGoogleAuthUrlQuery({
    return_to: window.location.href,
    platform: 'susu_closet'
  }, {
    skip: !shouldFetchGoogleUrl
  });

  useEffect(() => {
    if (shouldFetchGoogleUrl && googleAuthData && googleAuthData.authUrl) {
      setShouldFetchGoogleUrl(false);
      window.location.href = googleAuthData.authUrl;
    }
  }, [shouldFetchGoogleUrl, googleAuthData]);

  // Apple Auth (web)
  const { data: appleAuthData, isError: isAppleError, error: appleError } = useGetAppleAuthUrlQuery({
    return_to: window.location.href,
    platform: 'susu_closet'
  }, {
    skip: !shouldFetchAppleUrl
  });

  useEffect(() => {
    if (shouldFetchAppleUrl && appleAuthData && appleAuthData.authUrl) {
      setShouldFetchAppleUrl(false);
      window.location.href = appleAuthData.authUrl;
    }
  }, [shouldFetchAppleUrl, appleAuthData]);

  useEffect(() => {
    if (isAppleError && appleError) {
      setShouldFetchAppleUrl(false);
      setIsConnectingApple(false);
      setErrors({ general: 'Failed to connect to Apple. Please try again.' });
    }
  }, [isAppleError, appleError]);

  useEffect(() => {
    if (isGoogleError && googleError) {
      setIsConnectingGoogle(false);
      setShouldFetchGoogleUrl(false);
      setErrors({ general: 'Failed to connect to Google. Please try again.' });
    }
  }, [isGoogleError, googleError]);

  // Listen for native login success messages from Expo wrapper
  useEffect(() => {
    const handleMessage = async (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data && data.type === 'NATIVE_LOGIN_SUCCESS') {
          const { token } = data.payload;
          
          if (token) {
            try {
              // Decode token payload to determine provider
              let provider = 'google';
              try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.iss && payload.iss.includes('apple')) {
                  provider = 'apple';
                }
              } catch (e) {
                console.warn('Could not decode token to find provider, defaulting to google');
              }

              const res = await nativeLogin({
                token,
                provider,
                platform: 'susu_closet'
              }).unwrap();
              
              if (res.jwt) {
                dispatch(setAuthCredentials({ token: res.jwt, user: res.user }));
                if (onLogin) onLogin();
              }
            } catch (err) {
              console.error('Native login exchange failed', err);
              setErrors({ general: 'Login failed to verify with server.' });
              setIsConnectingGoogle(false);
              setIsConnectingApple(false);
            }
          }
        } else if (data && data.type === 'NATIVE_LOGIN_ERROR') {
          setIsConnectingGoogle(false);
          setIsConnectingApple(false);
          setErrors({ general: `Login failed: ${data.payload.error || 'Unknown error'}` });
        }
      } catch (e) {
        // Not a JSON message, ignore
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage);
    };
  }, [nativeLogin, dispatch, onLogin]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setMode('signin');
      setCredentials({ identifier: '', password: '' });
      setSignupData({ username: '', email: '', password: '', passwordConfirm: '' });
      setErrors({});
      setShowPassword(false);
      setSuccessMessage('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (mode === 'signin') {
      setCredentials(prev => ({ ...prev, [name]: value }));
    } else {
      setSignupData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (errors.general) setErrors(prev => ({ ...prev, general: '' }));
  };

  const validateSignin = () => {
    const newErrors = {};
    if (!credentials.identifier) newErrors.identifier = 'Username or email is required';
    if (!credentials.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSignup = () => {
    const newErrors = {};
    if (!signupData.username || signupData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    if (!signupData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!signupData.password || signupData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (signupData.password !== signupData.passwordConfirm) {
      newErrors.passwordConfirm = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignin = async (e) => {
    e.preventDefault();
    if (!validateSignin()) return;
    setErrors({});

    try {
      const result = await login({
        ...credentials,
        platform: 'susu_closet'
      }).unwrap();

      if (!result || !result.jwt) throw new Error('Login response missing token');

      // Set credentials in Redux (also persists to localStorage)
      dispatch(setAuthCredentials({ token: result.jwt, user: result.user }));

      if (onLogin) onLogin();
    } catch (err) {
      let errorMessage = 'Unable to log in. Please check your credentials and try again.';
      try {
        const msgs = err?.data?.message || err?.data?.data;
        if (Array.isArray(msgs)) {
          const msgObj = msgs[0]?.messages?.[0];
          if (msgObj?.id === 'Auth.form.error.invalid') {
            errorMessage = 'Invalid credentials. Please check your email/username and password.';
          } else if (msgObj?.message) {
            errorMessage = msgObj.message;
          }
        }
      } catch (_) { /* use default */ }
      setErrors({ general: errorMessage });
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;
    setErrors({});

    try {
      const result = await register({
        username: signupData.username,
        email: signupData.email,
        password: signupData.password,
        // Bot check on email/password signup. Google/Apple are unaffected -
        // they go through the OAuth callback, not /auth/local/register.
        'cf-turnstile-response': captchaToken
      }).unwrap();

      if (result?.jwt) {
        // Auto-login after registration — set credentials in Redux
        dispatch(setAuthCredentials({ token: result.jwt, user: result.user }));
        if (onLogin) onLogin();
      } else if (result?.user) {
        // Registration successful but needs email confirmation
        setSuccessMessage('Account created! Please check your email to confirm your account, then sign in.');
        setMode('signin');
        setCredentials({ identifier: signupData.email, password: '' });
      }
    } catch (err) {
      let errorMessage = 'Unable to create account. Please try again.';
      try {
        const msgs = err?.data?.message || err?.data?.data;
        if (Array.isArray(msgs)) {
          const msgObj = msgs[0]?.messages?.[0];
          if (msgObj?.message) errorMessage = msgObj.message;
        } else if (typeof err?.data?.message === 'string') {
          errorMessage = err.data.message;
        }
      } catch (_) { /* use default */ }
      setErrors({ general: errorMessage });
    } finally {
      // A Turnstile token is single-use - Cloudflare rejects a replay as
      // `timeout-or-duplicate`. Reset the widget whether we succeeded or not.
      setCaptchaToken('');
      setCaptchaReset(n => n + 1);
    }
  };

  const handleSocialLogin = (provider) => {
    const isNativeApp = window.ReactNativeWebView !== undefined;
    
    if (isNativeApp) {
      if (provider === 'google') setIsConnectingGoogle(true);
      if (provider === 'apple') setIsConnectingApple(true);
      
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: provider === 'google' ? 'LOGIN_GOOGLE' : 'LOGIN_APPLE',
        payload: {
          platform: 'susu_closet',
          return_to: window.location.href
        }
      }));
    } else {
      if (provider === 'google') {
        setIsConnectingGoogle(true);
        setShouldFetchGoogleUrl(true);
      } else if (provider === 'apple') {
        setIsConnectingApple(true);
        setShouldFetchAppleUrl(true);
      }
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };

  const switchMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setErrors({});
    setSuccessMessage('');
  };

  const isDisabled = isLoggingIn || isRegistering || isConnectingGoogle || isConnectingApple;

  return (
    <SusuModal
      open={isOpen}
      onClose={() => { if (onClose) onClose(); }}
      size="tiny"
      className="vto-login-confirmation-portal"
    >
      {isConnectingGoogle && (
        <Dimmer active inverted>
          <Loader>Connecting to Google...</Loader>
        </Dimmer>
      )}
      {isConnectingApple && (
        <Dimmer active inverted>
          <Loader>Connecting to Apple...</Loader>
        </Dimmer>
      )}

      <SusuModal.Content>
        <div className="signin-form-container">
          {/* Title */}
          <h2 className="signin-title">
            {mode === 'signin' ? 'Sign in to your account' : 'Create your account'}
          </h2>

          {/* Success message */}
          {successMessage && (
            <div className="signin-success-box">
              <p>{successMessage}</p>
            </div>
          )}

          {/* Error message */}
          {errors.general && (
            <div className="signin-error-box">
              <strong>Error</strong>
              <p>{errors.general}</p>
            </div>
          )}

          {/* Social Login */}
          <div className="signin-google-section" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              type="button"
              onClick={() => handleSocialLogin('google')}
              disabled={isDisabled}
              className="signin-google-btn"
            >
              <svg className="signin-google-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25526 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335" />
                <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4" />
                <path d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z" fill="#FBBC05" />
                <path d="M12.0004 24C15.2404 24 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2704 14.29L1.27539 17.385C3.25537 21.31 7.3104 24 12.0004 24Z" fill="#34A853" />
              </svg>
              <span>Continue with Google</span>
            </button>
            <button
              type="button"
              onClick={() => handleSocialLogin('apple')}
              disabled={isDisabled}
              className="signin-google-btn"
            >
              <svg className="signin-google-icon" viewBox="0 0 384 512" fill="currentColor">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
              </svg>
              <span>Continue with Apple</span>
            </button>
          </div>

          {/* Divider */}
          <div className="signin-divider">
            <div className="signin-divider-line" />
            <span className="signin-divider-text">
              {mode === 'signin' ? 'Or sign in with email' : 'Or sign up with email'}
            </span>
            <div className="signin-divider-line" />
          </div>

          {/* === SIGN IN FORM === */}
          {mode === 'signin' && (
            <form className="signin-form" onSubmit={handleSignin}>
              <div className="signin-field">
                <label className="signin-label" htmlFor="vto-identifier">
                  Username or Email <span className="signin-required">*</span>
                </label>
                <input
                  id="vto-identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username email"
                  required
                  value={credentials.identifier}
                  onChange={handleChange}
                  placeholder="Enter your username or email"
                  className={`signin-input ${errors.identifier ? 'signin-input-error' : ''}`}
                  disabled={isDisabled}
                />
                {errors.identifier && <p className="signin-field-error">{errors.identifier}</p>}
              </div>

              <div className="signin-field">
                <label className="signin-label" htmlFor="vto-password">
                  Password <span className="signin-required">*</span>
                </label>
                <div className="signin-password-wrapper">
                  <input
                    id="vto-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={credentials.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className={`signin-input ${errors.password ? 'signin-input-error' : ''}`}
                    disabled={isDisabled}
                  />
                  <button type="button" className="signin-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p className="signin-field-error">{errors.password}</p>}
              </div>

              <button type="submit" className="signin-submit-btn" disabled={isDisabled}>
                {isLoggingIn ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          )}

          {/* === SIGN UP FORM === */}
          {mode === 'signup' && (
            <form className="signin-form" onSubmit={handleSignup}>
              <div className="signin-field">
                <label className="signin-label" htmlFor="vto-username">
                  Username <span className="signin-required">*</span>
                </label>
                <input
                  id="vto-username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={signupData.username}
                  onChange={handleChange}
                  placeholder="Choose a username"
                  className={`signin-input ${errors.username ? 'signin-input-error' : ''}`}
                  disabled={isDisabled}
                />
                {errors.username && <p className="signin-field-error">{errors.username}</p>}
              </div>

              <div className="signin-field">
                <label className="signin-label" htmlFor="vto-email">
                  Email <span className="signin-required">*</span>
                </label>
                <input
                  id="vto-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={signupData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={`signin-input ${errors.email ? 'signin-input-error' : ''}`}
                  disabled={isDisabled}
                />
                {errors.email && <p className="signin-field-error">{errors.email}</p>}
              </div>

              <div className="signin-field">
                <label className="signin-label" htmlFor="vto-signup-password">
                  Password <span className="signin-required">*</span>
                </label>
                <div className="signin-password-wrapper">
                  <input
                    id="vto-signup-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={signupData.password}
                    onChange={handleChange}
                    placeholder="Create a password (min. 6 chars)"
                    className={`signin-input ${errors.password ? 'signin-input-error' : ''}`}
                    disabled={isDisabled}
                  />
                  <button type="button" className="signin-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p className="signin-field-error">{errors.password}</p>}
              </div>

              <div className="signin-field">
                <label className="signin-label" htmlFor="vto-password-confirm">
                  Confirm Password <span className="signin-required">*</span>
                </label>
                <input
                  id="vto-password-confirm"
                  name="passwordConfirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={signupData.passwordConfirm}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className={`signin-input ${errors.passwordConfirm ? 'signin-input-error' : ''}`}
                  disabled={isDisabled}
                />
                {errors.passwordConfirm && <p className="signin-field-error">{errors.passwordConfirm}</p>}
              </div>

              {/* Bot verification. Renders nothing when no site key is set. */}
              <TurnstileWidget
                action="register"
                onVerify={setCaptchaToken}
                resetSignal={captchaReset}
              />

              <button type="submit" className="signin-submit-btn" disabled={isDisabled}>
                {isRegistering ? 'Creating account...' : 'Sign up'}
              </button>
            </form>
          )}

          {/* Toggle sign-in / sign-up */}
          <div className="signin-signup-link">
            {mode === 'signin' ? (
              <>New here?{' '}<button type="button" className="signin-link-btn" onClick={switchMode}>Sign up with email</button></>
            ) : (
              <>Already have an account?{' '}<button type="button" className="signin-link-btn" onClick={switchMode}>Sign in</button></>
            )}
          </div>

          {/* Cancel button */}
          <button type="button" className="signin-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </SusuModal.Content>
    </SusuModal>
  );
};

export default VTOLoginConfirmationPortal;